import clone from 'clone';
import { defaultProduct } from '../valuesSlice';
import { isEqual, get } from 'lodash-es';
import { config } from '../../../../helpers';
import { sanitizeFloat, sanitizeId, sanitizeKr, sanitizePercentage, } from '../../../../helpers/sanitize';
function onProductChange(newValues, oldValues = undefined) {
    const sub_products = newValues.sub_products;
    const isSubProductsProduct = () => {
        return ['stair', 'grouped'].includes(newValues.type);
    };
    const getSubProductsAttributeSum = (attributeKey, precision = null) => {
        let result = 0;
        if (sub_products.length) {
            result = sub_products
                .map((sub_product) => {
                return (sanitizeFloat(sub_product[attributeKey], 4) *
                    sanitizeFloat(sub_product.amount, 4));
            })
                .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        }
        return precision ? sanitizeFloat(result, precision) : result;
    };
    const cost = isSubProductsProduct()
        ? getSubProductsAttributeSum('cost')
        : ![null, ''].includes(newValues.cost)
            ? sanitizeKr(newValues.cost)
            : '';
    const hasNegativeCost = () => cost && cost < 0;
    const surcharge_bottom_percentage = isSubProductsProduct()
        ? sanitizeFloat(newValues.surcharge_bottom_percentage, 4)
        : (() => {
            if (hasNegativeCost()) {
                return 0;
            }
            return sanitizeFloat(newValues.surcharge_bottom_percentage, 4);
        })();
    const surcharge_bottom_amount = isSubProductsProduct()
        ? getSubProductsAttributeSum('surcharge_bottom_amount', 2)
        : (() => {
            if (hasNegativeCost()) {
                return 0;
            }
            if (cost && surcharge_bottom_percentage) {
                return sanitizeFloat(cost * (surcharge_bottom_percentage / 100), 4);
            }
            return 0;
        })();
    const price_min_amount = isSubProductsProduct()
        ? getSubProductsAttributeSum('price_min_amount', 2)
        : (() => {
            const sanitized_cost = sanitizeKr(cost);
            return sanitized_cost && surcharge_bottom_percentage
                ? sanitizeKr(sanitized_cost + surcharge_bottom_amount)
                : sanitized_cost;
        })();
    const max_discount_initial_percentage = isSubProductsProduct()
        ? (() => {
            const max_discount_initial_amount = getSubProductsAttributeSum('max_discount_initial_amount', 2);
            const price_normal_amount = getSubProductsAttributeSum('price_normal_amount', 2);
            return sanitizeKr((max_discount_initial_amount / price_normal_amount) * 100);
        })()
        : (() => {
            if (hasNegativeCost()) {
                return 0;
            }
            return newValues.max_discount_initial_percentage;
        })();
    const price_normal_amount = isSubProductsProduct()
        ? getSubProductsAttributeSum('price_normal_amount', 2)
        : (() => {
            const sanitized_max_discount_initial_percentage = sanitizePercentage(max_discount_initial_percentage);
            return price_min_amount && sanitized_max_discount_initial_percentage
                ? sanitizeKr((price_min_amount /
                    (100 - sanitized_max_discount_initial_percentage)) *
                    100)
                : price_min_amount;
        })();
    const max_discount_initial_amount = price_normal_amount && max_discount_initial_percentage
        ? sanitizeKr(price_normal_amount *
            (sanitizePercentage(max_discount_initial_percentage) / 100))
        : 0;
    const surcharge_top_amount = sanitizeKr(price_normal_amount - price_min_amount);
    const surcharge_top_percentage = price_min_amount
        ? sanitizePercentage((price_normal_amount / price_min_amount - 1) * 100)
        : 0;
    const max_discount_calculated_percentage = max_discount_initial_percentage;
    const max_discount_calculated_amount = price_normal_amount && max_discount_calculated_percentage
        ? sanitizeKr(price_normal_amount *
            (sanitizePercentage(max_discount_calculated_percentage) / 100))
        : 0;
    const max_discount_override_percentage = hasNegativeCost()
        ? ''
        : newValues.max_discount_override_percentage;
    const max_discount_percentage = Math.min(sanitizePercentage(max_discount_calculated_percentage), ![null, ''].includes(max_discount_override_percentage)
        ? sanitizePercentage(max_discount_override_percentage)
        : sanitizePercentage(max_discount_calculated_percentage));
    const max_discount_amount = price_normal_amount * (max_discount_percentage / 100);
    const disabled_sub_products = (newValues.disabled_sub_products =
        (function () {
            const disabled_sub_products = newValues.sub_products.map((subProduct) => sanitizeId(subProduct.id));
            disabled_sub_products.push(sanitizeId(newValues.id));
            return disabled_sub_products;
        })());
    return Object.assign(Object.assign({}, newValues), { sub_products, cost: `${cost}`, surcharge_bottom_percentage: `${surcharge_bottom_percentage}`, surcharge_bottom_amount,
        surcharge_top_percentage,
        surcharge_top_amount,
        price_min_amount,
        price_normal_amount,
        max_discount_initial_amount, max_discount_initial_percentage: `${max_discount_initial_percentage}`, max_discount_calculated_percentage: sanitizePercentage(max_discount_calculated_percentage), max_discount_override_percentage,
        max_discount_calculated_amount,
        max_discount_percentage,
        max_discount_amount,
        disabled_sub_products });
}
function onSubProductChange(newValues, type, steps) {
    if ('stair' === type && 'variable' === newValues.variety) {
        newValues.amount = `${steps}`;
        newValues.amountIsEditable = false;
    }
    else {
        newValues.amountIsEditable = true;
    }
    newValues.amountEditableLabel = newValues.unit
        ? get(config('content.products.unit.labels'), newValues.unit, null)
        : null;
    return newValues;
}
let prevFormState = clone(defaultProduct);
function productFormValuesStateMachineReducer(formsState = defaultProduct, action) {
    if (!formsState || isEqual(formsState, prevFormState)) {
        return formsState;
    }
    formsState.sub_products = clone(formsState.sub_products).map((subProduct) => {
        return onSubProductChange(subProduct, formsState.type, ['stair', 'grouped'].includes(formsState.type)
            ? sanitizeFloat(formsState.steps, 4)
            : null);
    });
    formsState = onProductChange(formsState, prevFormState);
    prevFormState = clone(formsState);
    return formsState;
}
export default productFormValuesStateMachineReducer;
